import { PendingInboxItem } from "@forgepoint/shared";

/** A set of inbox items that share a thread (SDK) or workflow run, with shared header context. */
export interface InboxThreadGroupData {
  key: string;
  title: string;
  opportunityName: string | null;
  stageName: string | null;
  items: PendingInboxItem[];
}

/**
 * Groups inbox items by their thread (SDK tasks) or workflow run (workflow tasks).
 * Preserves the input order, so groups appear ordered by their most recent item and
 * items within a group keep that same recency order. The first non-null opportunity
 * and stage encountered in a group become its header context; since a thread belongs
 * to a single opportunity, every item in a group shares that context.
 * @param items - The inbox items to group, assumed pre-sorted by recency
 * @returns The grouped items, one entry per thread/run
 */
export function groupInboxItemsByThread(
  items: PendingInboxItem[],
): InboxThreadGroupData[] {
  const groups = new Map<string, InboxThreadGroupData>();
  for (const item of items) {
    const key = getGroupKey(item);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        key,
        title: getGroupTitle(item),
        opportunityName: item.opportunityName,
        stageName: item.stageName,
        items: [item],
      });
      continue;
    }
    existing.items.push(item);
    existing.opportunityName ??= item.opportunityName;
    existing.stageName ??= item.stageName;
  }
  return Array.from(groups.values());
}

/**
 * Returns the grouping key for an inbox item: the thread id for SDK tasks,
 * or the run id for workflow tasks.
 * @param item - The inbox item
 * @returns The group key
 */
function getGroupKey(item: PendingInboxItem): string {
  return item.sourceType === "agent_sdk_review" ? item.threadId : item.runId;
}

/**
 * Returns the header title for an item's group: the thread name for SDK tasks,
 * or the run/workflow name for workflow tasks.
 * @param item - The inbox item
 * @returns The group title
 */
function getGroupTitle(item: PendingInboxItem): string {
  if (item.sourceType === "agent_sdk_review") {
    return item.threadName ?? "Thread";
  }
  return item.runName ?? "Workflow";
}
